<template>
  <div ref="dropdownRef" class="compare-container">
    <div
      class="compare-rex-date-type-sel"
      v-if="!isCompareOptionsSelected"
      @click="toggleCompareOptions"
    >
      {{ $t('setting.compare') }}
      <button class="btn-" :id="`btn-arrow-down-compare`">
        <BaseIcon name="arrowDown" height="0.8em" width="0.8em" />
      </button>
    </div>
    <div class="compare-options" :class="{ selectHide: !showCompareOptions }">
      <div
        class="option"
        @click="comparePrevious"
        :class="{ disabled: disablePrevious }"
      >
        {{ $t('setting.comparePreviousPeriod') }}
      </div>
      <div class="option" @click="compareCustomDate">
        {{ $t('setting.compareCustomDate') }}
      </div>
    </div>
    <div class="compare-rex-service-sel" v-if="isCompareOptionsSelected">
      <div class="sel-time-disp">
        <BaseIcon
          class="selected-icon"
          v-if="enableIcon"
          :name="iconDetails.iconName"
          :width="iconDetails.iconWidth"
          :height="iconDetails.iconHeight"
        />
        <div class="selected-text" v-if="!isCompareCustom">
          {{ $t('setting.previous') }}
        </div>
        <div class="selected-text" v-else>
          {{ $t('setting.compare') }}
        </div>
      </div>
      <div class="compare-custom-sel-disp">
        <span class="bold-text mr-l-text">
          {{
            duration[0].toLocaleDateString('en-GB', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          }}
        </span>
        <span class="mr-l-text">
          {{
            duration[0].toLocaleTimeString('en', {
              timeStyle: 'short',
            })
          }}
        </span>

        <span class="gray-text mr-l-text">
          {{ $t('to') }}
        </span>
        <span class="bold-text mr-l-text">
          {{
            duration[1].toLocaleDateString('en-GB', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          }}
        </span>
        <span class="mr-l-text">
          {{
            duration[1].toLocaleTimeString('en', {
              timeStyle: 'short',
            })
          }}
        </span>
      </div>
      <div @click="closeCompareSelected" class="close-icon">
        <BaseIcon name="closeIcon" width="0.6rem" height="0.6rem" />
      </div>
    </div>
    <Card
      v-if="customOpen"
      :header="$t('setting.selectTheStartDate')"
      :disableCardBody="true"
      class="compare-custom-crd"
    >
      <template #header-actions>
        <div @click="onCustomClose">
          <BaseIcon
            name="closeIcon"
            height="0.838rem"
            width="0.838rem"
            class="custom-close-icon"
          />
        </div>
      </template>
      <template #card-body>
        <div class="compare-custom-crd-body">
          <div class="range-compare-txt">
            {{ $t('setting.selectRange') }}
          </div>
          <div class="dt-picker">
            <div class="input-body">
              <RexTimePicker
                @onDateChange="onCustomTimeChange"
                :dateValue="initialDate"
                :customPicker="customPicker"
              />
            </div>
          </div>

          <div class="crd-actions">
            <RexButtonV3
              variant="default"
              @onClick="onCustomCancel"
              id="compare-custom-cancel-btn"
            >
              {{ $t('cancel') }}
            </RexButtonV3>
            <RexButtonV3
              :disabled="!validTime"
              variant="primary"
              @onClick="onCustomApply"
              id="compare-custom-apply-btn"
              class="apply-btn"
            >
              {{ $t('apply') }}
            </RexButtonV3>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexTimePicker from '@/rex-components/RexTimePicker.vue';
import Card from '@/rex-components/rex-card.vue';
import dayjs from 'dayjs';
import { notificationError } from '@/utils/notification';
import { useI18n } from 'vue-i18n';

const store = useStore();
const showCompareOptions = ref(false);
const dropdownRef = ref(null);
const isPrevious = ref(false);
const compareCustomFrom = ref(new Date());
const compareCustomTo = ref(new Date());
const enableIcon = ref(true);
const validTime = ref(false);
const isCompareOptionsSelected = computed(
  () => store.state.globalState.settings.isCompareOptionsSelected,
);
const isCompareCustom = computed(
  () => store.state.globalState.settings.isCompareCustom,
);
const disablePrevious = computed(
  () => store.state.globalState.settings.disablePrevious,
);
const customOpen = ref(false);
const { t } = useI18n();
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  duration: {
    type: Array,
    default: () => [new Date(), new Date()],
  },
  selectedTimeLabel: {
    type: String,
    default: null,
  },
  iconDetails: {
    type: Object,
    default: () => ({}),
  },
  customPicker: {
    type: Boolean,
    default: true,
  },
});
const toggleCompareOptions = () => {
  showCompareOptions.value = !showCompareOptions.value;
  customOpen.value = false;
};
const emit = defineEmits(['compareCustom']);
const comparePrevious = () => {
  isPrevious.value = true;
  showCompareOptions.value = false;
  store.commit('globalState/settings/setIsCompareCustom', false);
  store.commit(
    'globalState/settings/setIsCompareOptionsSelected',
    !isCompareOptionsSelected.value,
  );
  enableIcon.value = true;
};
const selected = computed(() => {
  return props.selectedTimeLabel
    ? props.options.find((option) => option.name === props.selectedTimeLabel)
    : props.options[0];
});
const initialDate = computed(() => {
  return dayjs(props.duration[0]);
});

const closeCompareSelected = () => {
  store.commit('globalState/settings/setIsCompareCustom', false);
  store.commit(
    'globalState/settings/setIsCompareOptionsSelected',
    !isCompareOptionsSelected.value,
  );
  showCompareOptions.value = false;
};
const compareCustomDate = () => {
  customOpen.value = true;
  isPrevious.value = false;
  store.commit('globalState/settings/setIsCompareCustom', true);
};
const onCustomClose = () => {
  store.commit('globalState/settings/setIsCompareCustom', false);
  customOpen.value = false;
  showCompareOptions.value = false;
};
const onCustomTimeChange = function (startDate) {
  if (
    dayjs().isSame(startDate, 'day') &&
    (startDate.hour() > dayjs().hour() ||
      (startDate.hour() === dayjs().hour() &&
        startDate.minute() > dayjs().minute()))
  ) {
    validTime.value = false;
    notificationError(t('selectedEndTimeGreaterThanCurrent'));
    return;
  }
  compareCustomFrom.value = new Date(startDate.valueOf());
  validTime.value = true;
};
const onCustomApply = () => {
  emit('compareCustom', compareCustomFrom.value);
  customOpen.value = false;
  showCompareOptions.value = false;
  store.commit(
    'globalState/settings/setIsCompareOptionsSelected',
    !isCompareOptionsSelected.value,
  );
};
const onCustomCancel = function () {
  store.commit('globalState/settings/setIsCompareCustom', false);
  customOpen.value = false;
  showCompareOptions.value = false;
};
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showCompareOptions.value = false;
  }
};
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
.compare-rex-service-sel {
  display: flex;
  justify-content: center;
  align-items: center;
  //width: 37.0625em;
  text-align: left;
  outline: none;
  height: 2.5em;
  line-height: 1.375em;
  border-radius: 0.5rem;
  background-color: $color-black;
  color: $font-primary;
  border-radius: 0.5rem;
}
.compare-rex-date-type-sel {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: $color-black;
  color: $font-primary;
  border-radius: 0.5rem;
  width: 8rem;
}
.selected-text {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  color: #d1d1d1;
}

.selected {
  color: $font-primary;
  cursor: pointer;
  user-select: none;
  height: 2.5rem;
  display: flex;
  align-items: center;
}
.active {
  @include active-state;
}
.items {
  color: $font-primary;
  border-radius: 0.5rem;
  background-color: $bg-primary;
  width: 8.438em;
  border-right: 0.0625rem solid $border-primary;
  border-left: 0.0625rem solid $border-primary;
  border-bottom: 0.0625rem solid $border-primary;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  height: auto;
  @include active-state;
  overflow-y: auto;
  margin-top: 0.169rem;
}
.items > div:first-child {
  padding-top: 0.531rem;
}
.items > div:last-child {
  padding-bottom: 0.719rem;
}
.options {
  display: flex;
  justify-content: space-between;
}

.text-class {
  margin-top: 0.5rem;
  margin-bottom: 0.9375rem;
}
.compare-rex-date-type-sel .items div:hover {
  cursor: pointer;
}
.sel-time-disp {
  display: flex;
  align-items: center;
  width: fit-content;
}
.compare-custom-sel-disp {
  font-size: 0.87rem;
}
.selectHide {
  display: none;
}
.selected-icon {
  margin-left: 0.5rem;
  fill: #787979;
}
.bold-text {
  font-weight: 600;
}
.gray-text {
  color: #d1d1d1;
}
.mr-l-text {
  margin-left: 0.438rem;
}

:deep(.b-calendar .b-calendar-grid) {
  background: black;
  color: white;
  border: none !important ;
  box-shadow: none !important;
}

:deep(.b-form-btn-label-control.btn-group > .dropdown-menu) {
  transform: translate3d(-22px, 8px, 0px) scale(0.85) !important;
  background: black;
}
:deep(#to-cal__dialog_) {
  transform: translate3d(20px, 7px, 0px) scale(0.85) !important;
  //margin-left: -77px;
  right: 0;
  left: initial !important;
}

:deep(.b-calendar-grid-help) {
  display: none;
}
:deep(.b-calendar .b-calendar-grid [aria-disabled='true']) {
  background-color: $color-black !important;
  opacity: 0.6;
}
:deep(.b-calendar-grid-weekdays) {
  border: none !important;
  margin-bottom: 1rem;
}
:deep(.text-muted) {
  color: #f59600 !important;
  opacity: 0.8;
}
:deep(.text-dark) {
  color: #f59600 !important;
}
:deep(.dropdown-toggle) {
  background: $color-black;
  color: #f59600;
  border: none !important ;
}
:deep(.btn-outline-warning) {
  color: #f59600;
}
:deep(.btn-secondary:hover) {
  color: #f59600;
  background: $color-black;
}
:deep(.btn-secondary:focus) {
  color: #f59600;
  background: $color-black;
  border: none;
  box-shadow: none;
}
:deep(.show > .btn-secondary.dropdown-toggle) {
  color: #f59600;
  background: $color-black;
}
:deep(.card-header) {
  height: auto !important;
  font-size: 1rem;
  padding: 0.969rem;
}
.date-picker-warn {
  background-color: rgba($color: $color-brand-accent, $alpha: 0.3);
  padding: 1rem;
  border-left: 3px solid $color-brand-accent;
  color: $color-brand-accent;
  margin-top: 2rem;
}
.btn- {
  @include btn-fill($font-primary);
  padding: none;
  margin: 0px 0.275rem;
}
.compare-options {
  height: 5rem;
  width: 12rem;
  border-radius: 0.5rem;
  background-color: $bg-secondary;
  position: absolute;
  right: 0;
  z-index: 1;
  font-size: 0.875em;
  margin-top: 0.169rem;
  margin-right: 1rem;
  padding: 0.5rem;
  border: 1px solid #3d3f43;
  cursor: pointer;
}
.option {
  margin-bottom: 0.5rem;
  text-align: left;
  padding: 0.2rem;
}
.option:hover {
  color: $color-brand-accent;
}
.close-icon {
  fill: #bdbdbd;
  padding: 0.5rem;
}
.compare-custom-crd {
  height: auto;
  width: 550px;
  background-color: $bg-secondary;
  position: absolute;
  right: 0;
  z-index: 1;
  font-size: 0.875em;
  margin-top: 0.169rem;
  @include dropshadow-effect-secondary(0.27);
  margin-right: 1rem;
}
.compare-custom-crd-body {
  margin-left: 2.494rem;
  margin-right: 2.494rem;
}
.crd-actions {
  border-top: 0.0063em solid $border-primary;
  display: flex;
  justify-content: flex-end;
  padding-top: 0.656rem;
  margin-bottom: 0.844rem;
  align-items: center;
  gap: 1rem;
}
.dt-picker {
  height: 430px;
  padding-top: 2rem;
  width: fit-content;
}
.input-body {
  display: inline-flex;
}
.range-compare-txt {
  margin-top: 2rem;
  width: fit-content;
}
.custom-close-icon {
  cursor: pointer;
  fill: $stepper-disabled-grey;
  margin-right: 0.394rem;
}
:deep(.crd) {
  border-radius: 0;
}
.disabled {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
